<template>
  <div class="flow-button-group">
    <label
      v-if="title"
      class="flow-button-group__label"
      :class="customClassTitle"
    >
      {{ t(title) }}
      <i
        v-if="tooltipDesc"
        v-tooltip="{ value: t(tooltipDesc), class: tooltipClass || 'tooltip-icons-outside' }"
        class="fas fa-question-circle"
        data-html="true"
        data-toggle="tooltip"
        aria-hidden="true"
      />
    </label>
    <div
      class="flow-button-group__buttons"
      :class="[customClass, `flow-button-group__buttons--${layout}`]"
    >
      <button
        v-for="option in optionsValues"
        :key="option.value"
        type="button"
        class="flow-button-group__btn"
        :class="[
          `flow-button-group__btn--${mode}`,
          { 'flow-button-group__btn--selected': isSelected(option.value) }
        ]"
        @click="toggleOption(option.value)"
      >
        <i
          v-if="option.icon"
          :class="['far', option.icon, { 'fa-2x': mode === 'card' }]"
        />
        <div
          v-if="mode === 'card' || option.description"
          class="flow-button-group__content"
        >
          <span class="flow-button-group__label-text">{{ option.label }}</span>
          <slot name="option-description" :option="option">
            <span v-if="option.description" class="flow-button-group__description">
              {{ option.description }}
            </span>
          </slot>
        </div>
        <span v-else>{{ option.label }}</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, onMounted,
  PropType,
  ref,
  SetupContext,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { StringMap } from '@/types';
import Tooltip from 'primevue/tooltip';

export default defineComponent({
  name: 'ButtonGroup',

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: [Array, String, Number] as PropType<string[] | string | number>,
      required: true,
    },

    options: {
      type: Array as PropType<any[]>,
      required: true,
      default() {
        return [];
      },
    },

    title: {
      type: String,
      required: false,
      default: '',
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    tooltipDesc: {
      type: String,
      required: false,
      default: '',
    },

    tooltipClass: {
      type: String,
      required: false,
      default: '',
    },

    customClass: {
      type: String,
      required: false,
      default: '',
    },

    customClassTitle: {
      type: String,
      required: false,
      default: '',
    },

    mustTranslateLabel: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowEmpty: {
      type: Boolean,
      default: true,
    },

    mode: {
      type: String as PropType<'button' | 'card'>,
      default: 'button',
    },

    layout: {
      type: String as PropType<'horizontal' | 'vertical'>,
      default: 'horizontal',
    },
  },

  emits: ['update:modelValue'],

  setup(props: {
    modelValue: number | string | string[];
    options: any[];
    mustTranslateLabel: boolean;
    multiple: boolean;
    allowEmpty: boolean;
    mode: 'button' | 'card';
    layout: 'horizontal' | 'vertical';
  }, { emit }: SetupContext) {
    const { t } = useI18n();

    const data = ref(props.modelValue);
    const optionsValues = ref(props.options);

    watch([data], () => emit('update:modelValue', data.value));

    watch(() => props.modelValue, (newVal) => {
      data.value = newVal;
    });

    onMounted(() => {
      if (props.mustTranslateLabel) {
        optionsValues.value = optionsValues.value.map((item: any) => ({
          ...item,
          label: t(item.label),
        }));
      }
    });

    const isSelected = (value: string | number) => {
      if (props.multiple && Array.isArray(data.value)) {
        return data.value.includes(value as string);
      }
      return data.value === value;
    };

    const toggleOption = (value: string | number) => {
      if (props.multiple && Array.isArray(data.value)) {
        const index = data.value.indexOf(value as string);
        if (index === -1) {
          data.value = [...data.value, value as string];
        } else if (props.allowEmpty || data.value.length > 1) {
          data.value = data.value.filter((v) => v !== value);
        }
      } else {
        if (data.value === value && props.allowEmpty) {
          data.value = '';
        } else {
          data.value = value;
        }
      }
    };

    return {
      t,
      optionsValues,
      data,
      isSelected,
      toggleOption,
    };
  },
});
</script>

<style lang="scss" scoped>
.flow-button-group {
  &__label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  &__buttons {
    display: flex;
    gap: 0.5rem;

    &--vertical {
      flex-direction: column;
    }

    &--horizontal {
      flex-direction: row;
    }
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.15s ease;
    border: 1px solid $heather;
    border-radius: 4px;
    background: #fff;
    color: $tuna;
    padding: 0;

    i {
      color: $heather;
    }

    // Standard button mode
    &--button {
      flex: 1;
      height: $field-default-height;
      padding: 0.5rem 1rem;
      font-size: 0.875rem;

      i {
        font-size: 0.875rem;
      }
    }

    // Card mode
    &--card {
      flex: 1;
      flex-direction: row; // Internal items side-by-side or customize
      justify-content: flex-start;
      padding: 1rem;
      text-align: left;
      border-radius: 0.5rem;
      min-height: auto;

      // Icon customization for card
      > i {
        font-size: 1.25rem;
        margin-right: 0.5rem;
      }

      .flow-button-group__content {
        display: flex;
        flex-direction: column;
      }

      .flow-button-group__label-text {
        font-weight: 500;
        font-size: 0.875rem;
      }

      .flow-button-group__description {
        font-size: 0.75rem;
        color: #6b7280;
        margin-top: 0.125rem;
      }

      &:hover:not(.flow-button-group__btn--selected) {
        border-color: $brand-color-primary;
        background: rgba($brand-color-primary, 0.05);
      }
    }

    &:hover:not(&--selected) {
      background: #f2f2f2;
    }

    &--selected {
      background: $brand-color-primary;
      color: #fff;
      border-color: $brand-color-primary;

      i {
        color: #fff;
      }

      .flow-button-group__description {
        color: rgba(255, 255, 255, 0.9);
      }

      // Override hover for selected
      &:hover {
        background: $brand-color-primary; // Keep branding
      }
    }

    // Specific override: Card selected state often uses lighter bg with border in some designs,
    // but user requested "exact" match. The provided TriggerInternalEvent uses:
    // border-color: primary; background: rgba(primary, 0.1);
    // Let's adjust card selected style to match the user's reference image 2 style if possible.
    // Looking at TriggerInternalEvent/Form.vue css:
    // &--selected { border-color: $brand-color-primary; background: rgba($brand-color-primary, 0.1); }
    // So for card mode, we might want a different selected style (outlined) vs button mode (filled).

    &--card.flow-button-group__btn--selected {
      background: rgba($brand-color-primary, 0.1);
      color: $tuna; // Keep text dark usually
      border-color: $brand-color-primary;

      i {
        color: $brand-color-primary; // Icon becomes primary color
      }

      .flow-button-group__label-text {
        color: $brand-color-primary;
      }

      .flow-button-group__description {
         color: #6b7280; // Keep description gray or maybe primary-dark
      }
    }
  }
}
</style>
